"use client";
import { createContext, useContext } from "react";
import { useSession } from "./useSession";
import useQueryClient from "./useQueryClient";
import http from "@/libs/config/http";
import Cookies from "js-cookie";
import { useMutation } from "@tanstack/react-query";
import { getGuestId } from "@/libs/auth/getGuestId";
import { enqueueSnackbar } from "notistack";
const CheckoutContext = createContext();

const getCheckoutList = async () => {
  const guestId = getGuestId();
  const endpoint = guestId ? `guest/cart/${guestId}` : "account/cart";

  return await http(endpoint, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  });
};

const addToCartService = async ({ product_id, volume_id, count }) => {
  const guestId = getGuestId();

  const endpoint = guestId
    ? `guest/cart/${guestId}/store`
    : "account/cart/store";

  return await http(endpoint, {
    method: "POST",
    body: JSON.stringify({
      product_id,
      volume_id,
      count,
    }),
  });
};

const removeFromCartService = async ({ product_id, volume_id }) => {
  const guestId = getGuestId();
  const endpoint = guestId
    ? `guest/cart/${guestId}/${product_id}/${volume_id}`
    : `account/cart/${product_id}/${volume_id}`;

  return await http(endpoint, {
    method: "DELETE",
  });
};

const updateCartItemService = async ({ product_id, volume_id, count }) => {
  const guestId = getGuestId();

  const endpoint = guestId ? `guest/cart/${guestId}` : `account/cart`;

  return await http(endpoint, {
    method: "PUT",
    body: JSON.stringify({ product_id, volume_id, count }),
  });
};

const getCartCountService = async () => {
  const guestId = getGuestId();

  const endpoint = guestId
    ? `guest/cart/${guestId}/total`
    : "account/cart/total";

  return await http(endpoint, {
    method: "GET",
  });
};

// ----- && -------
export const CheckoutProvider = ({ children }) => {
  const { data, refetch } = useQueryClient({
    queryKey: ["get-all-cart-list"],
    queryFn: () => getCheckoutList(),
  });

  const { data: cartCountResponse, refetch: getCartCountRefetch } =
    useQueryClient({
      queryKey: ["cart-total"],
      queryFn: () => getCartCountService(),
    });

  const { mutate: addToCartMutate } = useMutation({
    mutationKey: ["add-to-cart"],
    mutationFn: addToCartService,
  });

  const { mutate: removeFromCartMutate } = useMutation({
    mutationKey: ["remove-from-cart"],
    mutationFn: removeFromCartService,
  });

  const { mutate: updateCartItemMutate } = useMutation({
    mutationKey: ["update-cart-item"],
    mutationFn: updateCartItemService,
  });

  const addToCart = async ({
    product_id,
    volume_id,
    count,
    succesMessage,
    errorMessage,
  }) =>
    addToCartMutate(
      { product_id, volume_id, count },
      {
        onSuccess: (data) => {
          refetch();
          getCartCountRefetch();
          // return data;
          enqueueSnackbar(succesMessage, { variant: "success" });
        },
        onError: (error) => {
          enqueueSnackbar(errorMessage, { variant: "error" });
        },
      }
    );

  const removeFromCart = async ({
    product_id,
    volume_id,
    succesMessage,
    errorMessage,
  }) => {
    removeFromCartMutate(
      {
        product_id,
        volume_id,
      },
      {
        onSuccess: (data) => {
          refetch();
          getCartCountRefetch();
          enqueueSnackbar(succesMessage, { variant: "success" });
        },
        onError: (error) => {
          enqueueSnackbar(errorMessage, { variant: "error" });
        },
      }
    );
  };

  const updateCartItem = async ({
    product_id,
    volume_id,
    count,
    succesMessage,
    errorMessage,
  }) => {
    updateCartItemMutate(
      {
        product_id,
        volume_id,
        count,
      },
      {
        onSuccess: (data) => {
          refetch();
          getCartCountRefetch();
          enqueueSnackbar(succesMessage, { variant: "success" });
        },
        onError: (error) => {
          enqueueSnackbar(errorMessage, { variant: "error" });
        },
      }
    );
  };

  return (
    <CheckoutContext.Provider
      value={{
        cartList: data?.results?.cart?.items ?? [],
        cartTotal: cartCountResponse?.results,
        addToCart,
        removeFromCart,
        updateCartItem,
        refetch,
        getCartCountRefetch,
      }}
    >
      {children}
    </CheckoutContext.Provider>
  );
};

export const useCheckout = () => useContext(CheckoutContext);
